import { useEffect, type CSSProperties } from 'react';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { useUIStore } from '../../stores/uiStore';
import { SHORTCUTS, formatShortcutKeys } from '../../onboarding/shortcutCatalog';
import type { ThemePalette } from '../../types';

interface ShortcutNudgeProps {
  palette: ThemePalette;
  glass: CSSProperties;
  hidden?: boolean;
}

export function ShortcutNudge({ palette, glass, hidden }: ShortcutNudgeProps) {
  const nudge = useOnboardingStore(s => s.currentNudge);
  const dismissNudge = useOnboardingStore(s => s.dismissNudge);
  const muteAction = useOnboardingStore(s => s.muteAction);
  const isCommandPaletteOpen = useUIStore(s => s.isCommandPaletteOpen);
  const isDistractionFree = useUIStore(s => s.isDistractionFree);
  const isSettingsOpen = useUIStore(s => s.isSettingsOpen);
  const isChordModalOpen = useUIStore(s => s.isChordModalOpen);
  const tutorialStatus = useOnboardingStore(s => s.tutorialStatus);

  const blocked = hidden || tutorialStatus === 'active' || isCommandPaletteOpen || isDistractionFree || isSettingsOpen || isChordModalOpen;
  const def = nudge ? SHORTCUTS[nudge.actionId] : null;

  useEffect(() => {
    if (!nudge || blocked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissNudge();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nudge, blocked, dismissNudge]);

  if (!nudge || !def || blocked) return null;

  return (
    <div
      className="fixed z-[450] pointer-events-none"
      style={{ right: 16, bottom: 72 }}
      data-shortcut-nudge={nudge.actionId}
    >
      <div
        className="pointer-events-auto shortcut-nudge-enter max-w-[300px] rounded-2xl border shadow-2xl backdrop-blur-md px-3 py-2.5 flex items-start gap-2.5"
        style={glass}
        role="status"
        aria-label={`${def.label}. Shortcut: ${formatShortcutKeys(def.keys, def.chord, def.hold)}. ${def.hint}`}
      >
        <div
          className="shortcut-nudge-glow mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold"
          style={{ background: 'rgba(99,102,241,0.22)', color: '#a5b4fc' }}
        >
          ⌨
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold leading-tight" style={{ color: palette.panelText }}>
            {def.label}
          </div>
          <div className="text-[10px] mt-0.5 leading-snug" style={{ color: palette.panelSubText }}>
            {def.hint}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {def.keys.map((key, i) => (
              <span key={`${key}-${i}`} className="inline-flex items-center gap-1">
                {def.chord && i > 0 && (
                  <span className="text-[9px]" style={{ color: palette.panelSubText }}>then</span>
                )}
                {def.hold && i > 0 && (
                  <span className="text-[9px]" style={{ color: palette.panelSubText }}>or</span>
                )}
                <kbd
                  className="shortcut-kbd-pop px-1.5 py-0.5 rounded text-[10px] font-mono border"
                  style={{
                    borderColor: palette.panelBorder,
                    color: palette.panelText,
                    background: 'rgba(99,102,241,0.12)',
                    animationDelay: `${80 + i * 70}ms`,
                  }}
                >
                  {formatShortcutKeys([key], false)}
                </kbd>
              </span>
            ))}
          </div>
        </div>
        <button
          type="button"
          className="flex-shrink-0 w-5 h-5 rounded-md text-[10px] hover:bg-white/10 transition-colors"
          style={{ color: palette.panelSubText }}
          title="Don't remind me for this shortcut"
          onClick={() => muteAction(nudge.actionId)}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
